import { AGENT_PRICE, type RoadmapView } from '@bas/shared'

/**
 * The Setup page's DEMO view — what `/api/setup/roadmap` returns, for the
 * unauthenticated showcase (`?demo=<key>`), which never signs in and so can
 * never read the real derivation.
 *
 * ⚠️ A FIXTURE OF THE SERVER'S OUTPUT, NOT A SECOND DERIVATION. Every field here
 * — each card's `status`, the focus on the first ready card, "Still to do", the
 * switched-off Outlook row, the default-colours note — is something the server
 * computes (agents/setupPresentation.ts, skillRoadmapFullView.ts). Nothing in the
 * page recomputes it, so the demo exercises the same renderer a client sees.
 *
 * It deliberately holds ONE card in every state (Susan's setup, 2026-09-28,
 * issues #9–#11), so the showcase and John's review can see them all at once.
 * Links are inert (`#`) — the demo acts on no account.
 */
const DEMO = '#'

const sampleCard = (heading: string, body: string) =>
  `<div style="font-family:system-ui,sans-serif;padding:16px"><p style="font-weight:700;margin:0 0 6px">${heading}</p><p style="margin:0;color:#475569">${body}</p></div>`

export const DEMO_ROADMAP_VIEW: RoadmapView = {
  firstName: 'Mike',
  greeting: '',
  doneCount: 1,
  total: 5,
  sections: [],
  stepsDone: 11,
  stepsTotal: 19,
  percent: 57,
  waitingCount: 3,
  sayLine: 'Good progress, Mike. 11 of 19 steps done. The ones marked in amber are waiting on you — everything else is mine.',
  artifactCss: '',
  stillToDo: [
    { catalogId: 'brand-assets', title: 'Your photos & reviews', href: DEMO },
    { catalogId: 'brand-look', title: 'Your look', href: DEMO },
  ],
  boxes: [
    {
      title: 'Your business information',
      items: [
        { label: 'Your answers about the business', state: 'done', kind: 'info' },
        { label: 'Google Calendar — mike@treasurevalleyplumbing.com', state: 'done' },
        { label: 'Set up how you get paid', state: 'now', action: { label: 'Connect', href: DEMO } },
      ],
      doneCount: 2,
      total: 3,
      status: { kind: 'needs-you', label: 'Needs you', needs: ['Set up how you get paid'] },
      offConnections: [
        {
          label: 'Outlook',
          why: 'your-choice',
          note: 'Off — you chose this in setup',
          turnOn: { label: 'Turn on', href: DEMO, preferences: { inbox: { enabled: true } } },
        },
      ],
    },
    {
      title: 'Your Google listing',
      skill: 'google',
      items: [
        { label: 'Your details for Google', state: 'done', kind: 'info' },
        { label: 'Google — Treasure Valley Plumbing', state: 'done', kind: 'connection', verifyUrl: DEMO, changeHref: DEMO },
        { label: 'Your go-ahead to start posting to Google', state: 'now', kind: 'goahead', buttonLabel: 'Approve', approve: { href: DEMO } },
      ],
      doneCount: 2,
      total: 3,
      status: { kind: 'ready', label: 'Ready for your go-ahead', line: 'Everything’s ready here — your go-ahead is the last step.', focus: true },
      made: [
        {
          title: 'A Google post',
          subtitle: 'Written for your listing, exactly as it will appear.',
          html: sampleCard('Winter is coming — is your water heater ready?', 'Book a 20-minute check before the first freeze.'),
          action: { kind: 'artifact' },
        },
      ],
    },
    {
      title: 'Your social accounts',
      skill: 'social',
      items: [
        { label: 'Facebook — Treasure Valley Plumbing', state: 'done', kind: 'connection', verifyUrl: DEMO, changeHref: DEMO },
        { label: 'Instagram', state: 'now', kind: 'connection', action: { label: 'Connect', href: DEMO } },
        {
          label: 'Your go-ahead to start posting',
          state: 'soon',
          kind: 'goahead',
          buttonLabel: 'Approve',
          blocked: { reason: 'Available once you connect Instagram', who: 'client' },
        },
      ],
      doneCount: 1,
      total: 3,
      status: { kind: 'needs-you', label: 'Needs you', needs: ['Instagram'] },
    },
    {
      title: 'Your customer follow-ups',
      skill: 'nurture',
      items: [
        { label: 'How you follow up', state: 'done', kind: 'info' },
        { label: 'Your go-ahead to start reaching out', state: 'now', kind: 'goahead', buttonLabel: 'Approve', approve: { href: DEMO } },
      ],
      doneCount: 1,
      total: 2,
      status: { kind: 'ready', label: 'Ready for your go-ahead', line: 'Everything’s ready here — your go-ahead is the last step.', after: 'Your Google listing' },
      made: [
        {
          title: 'A follow-up email',
          subtitle: 'Sent to you, exactly as your customer would get it.',
          html: sampleCard('How is your new water heater doing?', 'It has been six months — reply if anything sounds off.'),
          action: { kind: 'test-send', cta: 'Send test email', sendId: 'demo' },
        },
      ],
      followUpList: { count: 42, href: DEMO },
    },
    {
      title: 'Your phone assistant',
      skill: 'phone',
      items: [
        { label: 'How your calls get handled', state: 'done', kind: 'info' },
        { label: 'Texting registration', state: 'wait', kind: 'connection', wait: 'with the carriers — 1–2 weeks, their clock' },
        {
          label: 'Your go-ahead to start answering calls',
          state: 'wait',
          kind: 'goahead',
          buttonLabel: 'Approve',
          blocked: { reason: 'Once the carriers approve your texting — usually 1–2 weeks, their clock', who: 'external' },
        },
      ],
      doneCount: 1,
      total: 3,
      status: { kind: 'in-progress', label: 'Waiting on others', line: 'Once the carriers approve your texting — usually 1–2 weeks, their clock' },
    },
    {
      title: 'Your website',
      skill: 'web',
      items: [
        { label: 'What your site should say', state: 'done', kind: 'info' },
        { label: 'Your go-ahead to go live', state: 'done', kind: 'goahead', live: true },
      ],
      doneCount: 2,
      total: 2,
      live: true,
      status: { kind: 'live', label: 'Live' },
    },
    { title: 'Your ads', skill: 'ads', items: [], doneCount: 0, total: 0, price: `$${AGENT_PRICE}/mo` },
  ],
  paperwork: {
    sections: [
      {
        title: 'Your invoices & quotes',
        rows: [
          {
            title: 'An invoice',
            subtitle: 'The page your customer opens to pay.',
            html: '',
            action: { kind: 'artifact-page', href: DEMO, cta: 'Preview' },
            pending: { note: 'These are default colours — you haven’t picked yours yet.', cta: 'Pick your colours', href: DEMO },
          },
        ],
      },
    ],
  },
}
